import React, { useState } from "react";
import exportFromJSON from "export-from-json";
import moment from "moment";
import CITESGridRow from "./CITESGridRow";
import { IMicrochipRegistrationResponseModel } from "../../../typings/models/MicrochipRegistrationResponseModel";
import "../../../styles/gridStyle.css";
import downloadexcel from "../../../assets/export-2.png";
import Loader from "../../../components/molecules/Loader";

interface GridProps {
  data: IMicrochipRegistrationResponseModel[];
  isLoading: boolean;
  registrationType: number;
  scientificNames: Option[];
}

interface Option {
  value: string;
  label: string;
}

const CITESGrid: React.FC<GridProps> = ({
  data,
  isLoading,
  registrationType,
  scientificNames,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const itemsPerPage = 10;
  const maxPagesToShow = 5;
  // Filter the data based on the search query
  const filteredData =
    data.length > 0
      ? data.filter((item) =>
          Object.values(item).some((value) =>
            value?.toString().toLowerCase().includes(searchQuery?.toLowerCase())
          )
        )
      : [];

  // Function to perform sorting based on the current sort settings
  const sortedData = filteredData.sort((a, b) => {
    if (sortColumn === null) return 0;

    const aValue = a[sortColumn];
    const bValue = b[sortColumn];

    if (aValue < bValue) {
      return sortDirection === "asc" ? -1 : 1;
    } else if (aValue > bValue) {
      return sortDirection === "asc" ? 1 : -1;
    } else {
      return 0;
    }
  });

  // Calculate the total number of pages
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  // Get the data to display on the current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const dataToShow = sortedData.slice(startIndex, endIndex);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to the first page when searching
  };

  const findArrayElementByTitle = (
    value: string,
    scientificNames: Option[]
  ) => {
    var scientificName = scientificNames.find((el) => el.value === value);
    if (scientificName != undefined) return scientificName.label;
  };

  const nullCheckArrayElementByTitle = (value: string) => {
    var titleText = value === null ? "" : value;
    if (titleText != undefined) return titleText;
  };

  const nullCheckArrayElementByTitleNumber = (value: number) => {
    var titleText = value === null ? "" : value;
    if (titleText != undefined) return titleText;
  };

  const onClickExportToExcel = () => {
    if (data.length > 0) {
      const regData = data.map((row) => {
        if (registrationType === 1) {
          return {
            "Reference Number": row.registrationReferenceNumber,
            "Leg Ring Number": row.legRingTagMicrochipNo,
            "Name of Applicant": row.name,
            "Scientific Name": findArrayElementByTitle(
              row.scientificName,
              scientificNames
            ),
            "CITES Appendix Number": row.citesAppendix,
            "Foreign CITES Permit number": nullCheckArrayElementByTitle(
              row.foreignCITESPermitNo
            ),
            "Hatch Date": nullCheckArrayElementByTitle(row.hatchDateString),
          };
        } else if (registrationType === 2) {
          return {
            "Reference Number": row.registrationReferenceNumber,
            "Tag Number": row.legRingTagMicrochipNo,
            "Name of Applicant": row.name,
            "Scientific Name": findArrayElementByTitle(
              row.scientificName,
              scientificNames
            ),
            "CITES Appendix Number": row.citesAppendix,
            "Foreign CITES Permit number": nullCheckArrayElementByTitle(
              row.foreignCITESPermitNo
            ),
          };
        } else if (registrationType === 3) {
          return {
            "Reference Number": row.registrationReferenceNumber,
            "Microchip Number": row.legRingTagMicrochipNo,
            "Name of Applicant": row.name,
            "Batch Number": nullCheckArrayElementByTitle(row.batchNumberString),
            "Quantity for Registration": nullCheckArrayElementByTitleNumber(
              row.registeredQuantityinBatch
            ),
            "Harvest  Date": nullCheckArrayElementByTitle(
              row.harvestDateString
            ),
          };
        }
        return {
          "Reference Number": row.registrationReferenceNumber,
          "Leg Ring Tag Microchip No": row.legRingTagMicrochipNo,
          "Name of Applicant": row.name,
          "Scientific Name": row.scientificName,
          "CITES Appendix Number": row.citesAppendix,
          "Foreign CITES Permit number": row.foreignCITESPermitNo,
          "Hatch Date": row.hatchDateString,
        };
      });
      exportFromJSON({
        data: regData,
        fileName: "Microchip Registrations",
        exportType: exportFromJSON.types.xls,
      });
    }
  };

  const handlePagination = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSort = (column: string) => {
    if (column === sortColumn) {
      // If the same column is clicked again, toggle the sort direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // If a new column is clicked, set the new column and default to ascending sort
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const renderPageButtons = () => {
    const pageButtons = [];
    let startPage = 1;
    let endPage = totalPages;

    if (totalPages > maxPagesToShow) {
      const halfMaxPages = Math.floor(maxPagesToShow / 2);
      startPage = Math.max(currentPage - halfMaxPages, 1);
      endPage = startPage + maxPagesToShow - 1;

      if (endPage > totalPages) {
        endPage = totalPages;
        startPage = endPage - maxPagesToShow + 1;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePagination(i)}>
            {i}
          </button>
        </li>
      );
    }

    if (totalPages > maxPagesToShow && endPage < totalPages) {
      pageButtons.push(
        <li key="separator" className="page-item disabled">
          <button className="page-link">...</button>
        </li>
      );
      pageButtons.push(
        <li
          key={totalPages}
          className={`page-item ${currentPage === totalPages ? "active" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => handlePagination(totalPages)}
          >
            {totalPages}
          </button>
        </li>
      );
    }

    return pageButtons;
  };
  return (
    <div className="container">
      <input
        type="text"
        placeholder="Search..."
        value={searchQuery}
        onChange={handleSearch}
        className="form-control mb-3 search-input"
      />
      <div className="col-lg-2">
        <a
          href="#"
          onClick={() => onClickExportToExcel()}
          className="export-action"
        >
          <img src={downloadexcel} alt="" className="img-fluid" />
          Export Excel
        </a>
      </div>
      <table
        className="table table-bordered table-hover csp-table-scroll"
      >
        {/* Table header */}
        <thead>
          <tr>
            <th
              className="grid-header"
              //onClick={() => handleSort("registrationReferenceNumber")}
            >
              Reference Number{" "}
              {/*{sortColumn === "registrationReferenceNumber" && (
                <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
              )}*/}
            </th>
            {registrationType === 1 && (
              <th
                className="grid-header"
                //onClick={() => handleSort("legRingTagMicrochipNo")}
              >
                Leg Ring Number{" "}
                {/* {sortColumn === "legRingTagMicrochipNo" && (
                  <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
                )} */}
              </th>
            )}
            {registrationType === 2 && (
              <th
                className="grid-header"
                //onClick={() => handleSort("legRingTagMicrochipNo")}
              >
                Tag Number{" "}
                {/* {sortColumn === "legRingTagMicrochipNo" && (
                  <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
                )} */}
              </th>
            )}
            {registrationType === 3 && (
              <th
                className="grid-header"
                //onClick={() => handleSort("legRingTagMicrochipNo")}
              >
                Microchip Number{" "}
                {/* {sortColumn === "legRingTagMicrochipNo" && (
                  <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
                )} */}
              </th>
            )}
            <th
              className="grid-header"
              //onClick={() => handleSort("name")}
            >
              Name of Applicant{" "}
              {/* {sortColumn === "name" && (
                <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
              )} */}
            </th>
            {(registrationType === 1 || registrationType === 2) && (
              <th
                className="grid-header"
                //onClick={() => handleSort("scientificName")}
              >
                Scientific Name{" "}
                {/* {sortColumn === "scientificName" && (
                  <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
                )} */}
              </th>
            )}
            {(registrationType === 1 || registrationType === 2) && (
              <th
                className="grid-header"
                //onClick={() => handleSort("citesAppendix")}
              >
                CITES Appendix Number{" "}
                {/* {sortColumn === "citesAppendix" && (
                  <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
                )} */}
              </th>
            )}
            {registrationType === 3 && (
              <th
                className="grid-header"
                //onClick={() => handleSort("batchNumber")}
              >
                Batch Number{" "}
                {/* {sortColumn === "batchNumber" && (
                  <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
                )} */}
              </th>
            )}
            {registrationType === 3 && (
              <th
                className="grid-header"
                //onClick={() => handleSort("registeredQuantityinBatch")}
              >
                Quantity for Registration{" "}
                {/* {sortColumn === "registeredQuantityinBatch" && (
                  <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
                )} */}
              </th>
            )}
            {(registrationType === 1 || registrationType === 2) && (
              <th
                className="grid-header"
                //onClick={() => handleSort("foreignCITESPermitNo")}
              >
                Foreign CITES Permit number{" "}
                {/* {sortColumn === "foreignCITESPermitNo" && (
                <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
              )} */}
              </th>
            )}
            {registrationType === 3 && (
              <th
                className="grid-header"
                //onClick={() => handleSort("harvestDate")}
              >
                Harvest Date{" "}
                {/* {sortColumn === "harvestDate" && (
                  <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
                )} */}
              </th>
            )}
            {registrationType === 1 && (
              <th
                className="grid-header"
                //onClick={() => handleSort("hatchDate")}
              >
                Hatch Date{" "}
                {/* {sortColumn === "hatchDate" && (
                  <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
                )} */}
              </th>
            )}
            <th className="grid-header">Action</th>
            {/* Add other header columns here */}
          </tr>
        </thead>
        <tbody>
          {dataToShow.map((item) => (
            <CITESGridRow
              key={item.inspectionCaseNumber}
              data={item}
              registrationType={registrationType}
              scientificNames={scientificNames}
            />
          ))}
        </tbody>
      </table>
      {isLoading && <Loader />}

      {/* Pagination */}
      <nav aria-label="Page navigation example">
        <ul className="pagination">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => handlePagination(currentPage - 1)}
            >
              Previous
            </button>
          </li>
          {renderPageButtons()}
          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => handlePagination(currentPage + 1)}
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

export default CITESGrid;
